import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, AlertCircle, Clock, Eye, X, BarChart3 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../ui/Toast';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { notificacionBalanceService, NotificacionBalanceConDetalle } from '../../services/notificacionBalanceService';
import { PeriodoBalance } from '../../services/balanceService';
import type { Movimiento } from '../../lib/supabase';
import type { ObjecionComprobante } from '../../types/comprobante';
import { CuotaIglesiaConfirmDialog } from './CuotaIglesiaConfirmDialog';
import { DialogShell } from '../ui/DialogShell';
import { Textarea } from '../ui/Textarea';
import { ComprobanteSoloLectura } from '../comprobantes/ComprobanteSoloLectura';
import { ObjecionesComprobantes } from '../comprobantes/ObjecionesComprobantes';
import { mensajeErrorDeNegocio } from '../../utils/erroresDeNegocio';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { ambitoRecepcionBalance, type AmbitoRecepcionBalance } from '../../utils/permissions';
import { FormErrorAlert } from '../ui/FormErrorAlert';

interface NotificacionesBalanceListProps {
  readonly onVerDesglose?: (entidadId: string, tipoEntidad: 'lumisial' | 'diocesis', periodo: PeriodoBalance, entidadNombre: string, mostrarTodos: boolean) => void;
  readonly onVerBalance?: (tipoEntidad: 'lumisial' | 'diocesis', periodo: PeriodoBalance) => void;
  readonly onUpdate: () => void;
  readonly provinciaSeleccionada?: string;
}

const FORMATO_MONEDA = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' });
const formatCurrency = (amount: number) => FORMATO_MONEDA.format(amount);

/** La cuota iglesia que el nivel inferior debía transferir en el mes, con el comprobante de esa transferencia. */
const CuotaIglesiaDeLaRendicion: React.FC<{ readonly notif: NotificacionBalanceConDetalle }> = ({ notif }) => {
  if (notif.cuota_iglesia_por_traspasar <= 0 && !notif.comprobante_transferencia) {
    return <p className="text-sm text-slate-600 mb-4">Cuota iglesia: $0 — sin transferencia</p>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm mb-4">
      <span className="text-slate-600">
        Cuota iglesia: <span className="font-semibold">{formatCurrency(notif.cuota_iglesia_por_traspasar)}</span>
      </span>
      <ComprobanteSoloLectura
        etiqueta="Transferencia"
        comprobante={notif.comprobante_transferencia ?? null}
        textoSinComprobante="Transferencia sin comprobante en marcha blanca"
      />
    </div>
  );
};

export const NotificacionesBalanceList: React.FC<NotificacionesBalanceListProps> = ({
  onVerDesglose,
  onVerBalance,
  onUpdate,
  provinciaSeleccionada,
}) => {
  const { usuario } = useAuth();
  const toast = useToast();
  const { error, clearError, handleError } = useErrorHandler();
  const [notificaciones, setNotificaciones] = useState<NotificacionBalanceConDetalle[]>([]);
  const [loading, setLoading] = useState(() => ambitoRecepcionBalance(usuario, provinciaSeleccionada) !== null);

  const [selectedNotif, setSelectedNotif] = useState<string | null>(null);
  const [showObservacionModal, setShowObservacionModal] = useState(false);
  const [notaObservacion, setNotaObservacion] = useState('');
  // Los movimientos del mes de la notificación que se observa, para ofrecer qué comprobantes objetar.
  const [movimientosCargados, setMovimientosCargados] = useState<{ readonly notifId: string; readonly movimientos: readonly Movimiento[] } | null>(null);
  const objecionesRef = useRef<readonly ObjecionComprobante[]>([]);
  const handleObjeciones = useCallback((marcadas: readonly ObjecionComprobante[]) => { objecionesRef.current = marcadas; }, []);
  const [submitting, setSubmitting] = useState(false);
  const [cuotaDialogData, setCuotaDialogData] = useState<{
    tipo: 'jad' | 'jan';
    entidadNombre: string;
    mes: number;
    anio: number;
    montoAJAL?: number;
    montoPA?: number;
    montoRegular?: number;
    montoPAConfirmados?: number;
  } | null>(null);

  const ambito = ambitoRecepcionBalance(usuario, provinciaSeleccionada);
  const claveAmbito = ambito ? JSON.stringify(ambito) : '';
  // Sube tras recibir u observar para recargar sin volver a mostrar la carga.
  const [recarga, setRecarga] = useState(0);
  const loadNotificaciones = useCallback(() => { setRecarga(r => r + 1); }, []);

  // Cambiar de ámbito (otra provincia seleccionada) sí vuelve a mostrar la carga. Se detecta
  // durante el render, no en el efecto.
  const [claveCargada, setClaveCargada] = useState(claveAmbito);
  if (claveCargada !== claveAmbito) {
    setClaveCargada(claveAmbito);
    setNotificaciones([]);
    setLoading(claveAmbito !== '');
  }

  useEffect(() => {
    if (!claveAmbito) return;
    const filtros = JSON.parse(claveAmbito) as AmbitoRecepcionBalance;
    let vigente = true;
    notificacionBalanceService.getNotificacionesRecibidas(filtros)
      .then(notifs => { if (vigente) setNotificaciones(notifs); })
      .catch((err: unknown) => { reportarErrorInesperado(err, 'No pudimos cargar las notificaciones.'); })
      .finally(() => { if (vigente) setLoading(false); });
    return () => { vigente = false; };
  }, [claveAmbito, recarga]);

  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];


  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'enviado':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'recibido':
        return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'observado':
        return <AlertCircle className="w-5 h-5 text-amber-600" />;
      default:
        return null;
    }
  };

  const getEstadoBadgeClass = (estado: string) => {
    switch (estado) {
      case 'enviado':
        return 'bg-blue-100 text-blue-800';
      case 'recibido':
        return 'bg-emerald-100 text-emerald-800';
      case 'observado':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getEstadoLabel = (estado: string) => {
    switch (estado) {
      case 'enviado':
        return 'Pendiente';
      case 'recibido':
        return 'Recibido';
      case 'observado':
        return 'Observado';
      default:
        return estado;
    }
  };

  const handleMarcarRecibida = async (notif: NotificacionBalanceConDetalle) => {
    if (!usuario) {
      handleError('No se pudo identificar al usuario actual');
      return;
    }

    try {
      setSubmitting(true);
      const result = await notificacionBalanceService.marcarRecibidaConCuotaIglesia(notif.id);
      // Actualización optimista inmediata para feedback visual al usuario
      setNotificaciones(prev => prev.map(n => n.id === notif.id ? { ...n, estado: 'recibido' as const } : n));
      toast.success('Notificación marcada como recibida');
      loadNotificaciones();
      onUpdate();
      const { montoAJAL = 0, montoPA = 0, montoRegular = 0, montoPAConfirmados = 0 } = result;
      if (notif.tipo_entidad === 'lumisial' && (montoAJAL > 0 || montoPA > 0)) {
        setCuotaDialogData({ tipo: 'jad', entidadNombre: notif.entidad_nombre, mes: notif.periodo_mes, anio: notif.periodo_anio, montoAJAL, montoPA });
      } else if (notif.tipo_entidad === 'diocesis' && (montoRegular > 0 || montoPAConfirmados > 0)) {
        setCuotaDialogData({ tipo: 'jan', entidadNombre: notif.entidad_nombre, mes: notif.periodo_mes, anio: notif.periodo_anio, montoRegular, montoPAConfirmados });
      }
    } catch (err) {
      reportarErrorInesperado(err, 'Error al actualizar la notificación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAbrirObservacion = (notif: NotificacionBalanceConDetalle) => {
    setSelectedNotif(notif.id);
    setNotaObservacion('');
    objecionesRef.current = [];
    setShowObservacionModal(true);
    clearError();
    notificacionBalanceService
      .getMovimientosCompletosEntidad(notif.tipo_entidad, notif.entidad_id, notif.periodo_mes, notif.periodo_anio)
      .then(({ movimientos_balance, movimientos_no_balance }) => {
        setMovimientosCargados({ notifId: notif.id, movimientos: [...movimientos_balance, ...movimientos_no_balance] });
      })
      .catch((err: unknown) => { reportarErrorInesperado(err, 'No pudimos cargar los comprobantes del mes para objetarlos.'); });
  };

  const handleCerrarObservacion = () => {
    setShowObservacionModal(false);
    setSelectedNotif(null);
    setNotaObservacion('');
    objecionesRef.current = [];
    setMovimientosCargados(null);
    clearError();
  };

  const handleMarcarObservada = async () => {
    if (!usuario || !selectedNotif) {
      handleError('No se pudo identificar al usuario actual');
      return;
    }

    if (!notaObservacion.trim()) {
      handleError('Debes agregar una nota de observación');
      return;
    }

    try {
      setSubmitting(true);
      const notifId = selectedNotif;
      // Siempre por la operación con objeciones: sin ninguna marcada, observar es solo la nota.
      await notificacionBalanceService.marcarObservada(notifId, notaObservacion.trim(), objecionesRef.current);
      // Actualización optimista inmediata
      setNotificaciones(prev => prev.map(n => n.id === notifId ? { ...n, estado: 'observado' as const, nota_observacion: notaObservacion.trim() } : n));
      toast.success('Notificación marcada como observada');
      setShowObservacionModal(false);
      setSelectedNotif(null);
      setNotaObservacion('');
      objecionesRef.current = [];
      setMovimientosCargados(null);
      loadNotificaciones();
      onUpdate();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'Error al actualizar la notificación');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerDesglose = (notif: NotificacionBalanceConDetalle) => {
    if (onVerDesglose) {
      const tipoEntidad = notif.tipo_entidad as 'lumisial' | 'diocesis';
      const periodo: PeriodoBalance = {
        tipo: 'mensual',
        año: notif.periodo_anio,
        mes: notif.periodo_mes
      };
      onVerDesglose(notif.entidad_id, tipoEntidad, periodo, notif.entidad_nombre, true);
    }
  };

  const handleVerBalance = (notif: NotificacionBalanceConDetalle) => {
    if (onVerBalance) {
      const tipoEntidad = notif.tipo_entidad as 'lumisial' | 'diocesis';
      const periodo: PeriodoBalance = {
        tipo: 'mensual',
        año: notif.periodo_anio,
        mes: notif.periodo_mes
      };
      onVerBalance(tipoEntidad, periodo);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
      </div>
    );
  }

  if (notificaciones.length === 0) {
    return (
      <div className="bg-slate-50 rounded-lg p-8 text-center">
        <Eye className="w-12 h-12 text-slate-400 mx-auto mb-3" />
        <p className="text-slate-600">No hay notificaciones de balance</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {notificaciones.map((notif) => (
          <div
            key={notif.id}
            className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {getEstadoIcon(notif.estado)}
                <div>
                  <h3 className="font-bold text-slate-800">{notif.entidad_nombre}</h3>
                  <p className="text-sm text-slate-500">
                    {meses[notif.periodo_mes - 1]} {notif.periodo_anio}
                  </p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getEstadoBadgeClass(notif.estado)}`}>
                {getEstadoLabel(notif.estado)}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-xs text-emerald-600 font-medium mb-1">Ingresos</p>
                <p className="text-lg font-bold text-emerald-700">{formatCurrency(notif.total_ingresos)}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-xs text-red-600 font-medium mb-1">Egresos</p>
                <p className="text-lg font-bold text-red-700">{formatCurrency(notif.total_egresos)}</p>
              </div>
              <div className={`rounded-lg p-3 ${notif.balance_neto >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}>
                <p className={`text-xs font-medium mb-1 ${notif.balance_neto >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                  Balance
                </p>
                <p className={`text-lg font-bold ${notif.balance_neto >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                  {formatCurrency(notif.balance_neto)}
                </p>
              </div>
            </div>

            {notif.total_financiamiento > 0 && (
              <p className="text-sm text-slate-600 mb-4">
                Financiamiento recibido: <span className="font-semibold">{formatCurrency(notif.total_financiamiento)}</span>
                {' '}(no cuenta como ingreso; suma al balance)
              </p>
            )}

            <CuotaIglesiaDeLaRendicion notif={notif} />

            {notif.nota && (
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Nota:</span> {notif.nota}
                </p>
              </div>
            )}

            {notif.nota_observacion && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Observación:</span> {notif.nota_observacion}
                </p>
              </div>
            )}

            <div className="text-xs text-slate-500 mb-4">
              Enviado el {new Date(notif.fecha_envio).toLocaleDateString('es-CL')}
            </div>

            <div className="flex flex-wrap gap-3">
              {onVerDesglose && (
                <button
                  onClick={() => handleVerDesglose(notif)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  <Eye className="w-4 h-4" />
                  Ver Desglose
                </button>
              )}
              {onVerBalance && (
                <button
                  onClick={() => handleVerBalance(notif)}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-semibold"
                >
                  <BarChart3 className="w-4 h-4" />
                  Ver Balance
                </button>
              )}
              
              {notif.estado === 'enviado' && (
                <>
                  <button
                    onClick={() => handleMarcarRecibida(notif)}
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Marcar como Recibido
                  </button>
                  <button
                    onClick={() => handleAbrirObservacion(notif)}
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    <AlertCircle className="w-4 h-4" />
                    Marcar con Observación
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <DialogShell
        isOpen={showObservacionModal}
        onClose={handleCerrarObservacion}
        size="md"
        header={(
          <div className="sticky top-0 z-10 border-b border-slate-200 p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-amber-100 p-2 rounded-lg">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">Agregar Observación</h2>
            </div>
            <button
              onClick={handleCerrarObservacion}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-6 h-6 text-slate-600" />
            </button>
          </div>
        )}
      >
        <div className="space-y-4">
          <FormErrorAlert error={error} onClear={clearError} />

          <Textarea
            label="Nota de Observación"
            value={notaObservacion}
            onChange={(e) => setNotaObservacion(e.target.value)}
            placeholder="Describe las observaciones sobre este balance..."
            rows={4}
            required
          />

          {selectedNotif && movimientosCargados?.notifId === selectedNotif && (
            <ObjecionesComprobantes key={selectedNotif} movimientos={movimientosCargados.movimientos} onChange={handleObjeciones} />
          )}

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={handleCerrarObservacion}
              className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleMarcarObservada}
              className="flex-1 px-6 py-3 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting ? 'Guardando...' : 'Guardar Observación'}
            </button>
          </div>
        </div>
      </DialogShell>

      {cuotaDialogData && (
        <CuotaIglesiaConfirmDialog
          isOpen={true}
          onClose={() => setCuotaDialogData(null)}
          tipo={cuotaDialogData.tipo}
          entidadNombre={cuotaDialogData.entidadNombre}
          mes={cuotaDialogData.mes}
          anio={cuotaDialogData.anio}
          montoAJAL={cuotaDialogData.montoAJAL}
          montoPA={cuotaDialogData.montoPA}
          montoRegular={cuotaDialogData.montoRegular}
          montoPAConfirmados={cuotaDialogData.montoPAConfirmados}
        />
      )}
    </>
  );
};
